import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Post } from '../../shared/types/post';
import { getUserPostsPath } from '../../shared/config/env';
import './ArticleLinkCard.css';

interface ArticleLinkCardProps {
  post: Post;
  lang: 'ja' | 'en';
  url: string; // 元のURL（ハッシュやクエリパラメータを含む）
}

function ArticleLinkCard({ post, lang, url }: ArticleLinkCardProps) {
  const [imageError, setImageError] = useState<boolean>(false);
  const userPostsPath = getUserPostsPath();

  // サムネイルURLを検証（PostCardと同じロジック）
  const isValidThumbnailUrl = useMemo(() => {
    if (!post.thumbnail_url) return false;
    
    let url = post.thumbnail_url.trim();
    if (!url) return false;
    
    let fullUrl: string;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      fullUrl = url;
    } else if (url.startsWith('//')) {
      fullUrl = `https:${url}`;
    } else if (url.includes('.')) {
      fullUrl = `https://${url}`;
    } else {
      return false;
    }
    
    try {
      const urlObj = new URL(fullUrl);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }
      
      // TODO: サムネイル画像保管機能実装後、このチェックを削除
      if (urlObj.hostname === 'example.com' || urlObj.hostname.endsWith('.example.com')) {
        return false;
      }
      if (urlObj.hostname === 'via.placeholder.com') {
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }, [post.thumbnail_url]);

  const handleImageError = () => {
    setImageError(true);
  };

  // 表示するサムネイルURLを決定
  // サムネイルがない場合は/favicon.svgを使用
  const displayThumbnailUrl = useMemo(() => {
    if (isValidThumbnailUrl && !imageError && post.thumbnail_url) {
      let url = post.thumbnail_url.trim();
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      } else if (url.startsWith('//')) {
        return `https:${url}`;
      } else if (url.includes('.')) {
        return `https://${url}`;
      }
    }
    // サムネイルがない場合はファビコンを使用
    return '/favicon.svg';
  }, [isValidThumbnailUrl, imageError, post.thumbnail_url]);

  // リンク先URLを構築（元のURLのハッシュやクエリパラメータを保持）
  const linkUrl = useMemo(() => {
    const baseUrl = `/${lang}${userPostsPath}/${post.slug}`;
    try {
      const urlObj = new URL(url, window.location.origin);
      const hash = urlObj.hash;
      const search = urlObj.search;
      return `${baseUrl}${search}${hash}`;
    } catch {
      // URL解析に失敗した場合はベースURLのみ
      return baseUrl;
    }
  }, [lang, userPostsPath, post.slug, url]);

  return (
    <div className="article-link-card">
      <Link to={linkUrl} className="article-link-card__link">
        <div className="article-link-card__thumbnail">
          <img
            src={displayThumbnailUrl}
            alt={post.title}
            className="article-link-card__thumbnail-image"
            onError={handleImageError}
          />
        </div>
        <div className="article-link-card__content">
          <h3 className="article-link-card__title">{post.title}</h3>
          {post.excerpt && (
            <p className="article-link-card__excerpt">{post.excerpt}</p>
          )}
        </div>
      </Link>
    </div>
  );
}

export default ArticleLinkCard;
